import React, { useState, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

const HorizontalBarTimeline = ({ data, title, width, height }) => {
  const { t } = useTranslation();
  const [hoveredTrip, setHoveredTrip] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="text.secondary">
          {t("No trip timeline data available")}
        </Typography>
      </Box>
    );
  }

  // Format X-axis labels to show time
  const formatXAxisLabel = (tickItem) => {
    const hours = Math.floor(tickItem / 60);
    const minutes = tickItem % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  // Create a custom component to render multiple bars per vehicle
  const VehicleTripsBar = (props) => {
    const { payload, x, y, width, height } = props;
    const vehicle = data.find((d) => d.vehicleName === payload.vehicleName);

    if (!vehicle || !vehicle.trips) return null;

    const barHeight = height;
    const totalMinutes = 1440; // 24 hours

    const handleMouseMove = (trip, event) => {
      // Get the bounding rect of the chart container
      const rect = event.currentTarget.closest("svg").getBoundingClientRect();
      setHoveredTrip({ ...trip, vehicleName: vehicle.vehicleName });
      setMousePosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    };

    const handleMouseLeave = () => {
      setHoveredTrip(null);
    };

    return (
      <g>
        {vehicle.trips.map((trip, index) => {
          const tripStartX = x + (trip.startMinutes / totalMinutes) * width;
          const tripWidth = (trip.duration / totalMinutes) * width;

          return (
            <rect
              key={index}
              x={tripStartX}
              y={y}
              width={tripWidth}
              height={barHeight}
              fill={trip.color}
              stroke="#fff"
              strokeWidth={1}
              style={{ cursor: "pointer" }}
              onMouseMove={(e) => handleMouseMove(trip, e)}
              onMouseLeave={handleMouseLeave}
            />
          );
        })}
      </g>
    );
  };

  // Transform data for chart - one entry per vehicle
  const chartData = data.map((vehicle) => ({
    vehicleName: vehicle.vehicleName,
    vehicleNumber: vehicle.vehicleNumber,
    totalDuration: 1440, // Full day width
    trips: vehicle.trips,
  }));

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        flex: 1,
        position: "relative",
        mt: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          textAlign: "center",
          mb: 2,
          color: "#183059",
          fontWeight: 600,
          fontSize: "16px",
        }}
      >
        {t(title)}
      </Typography>

      <ResponsiveContainer width="100%" height={height || 350}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
          barCategoryGap={10}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            type="number"
            domain={[0, 1440]} // 24 hours in minutes
            tickFormatter={formatXAxisLabel}
            ticks={[0, 240, 480, 720, 960, 1200, 1440]} // Every 4 hours
            tick={{ fontSize: 10, fill: "#666" }}
            axisLine={{ stroke: "#e0e0e0" }}
          />
          <YAxis
            type="category"
            dataKey="vehicleName"
            width={100}
            tick={{ fontSize: 12, fill: "#666" }}
            axisLine={{ stroke: "#e0e0e0" }}
          />

          {/* Custom bar that will render all trips for each vehicle */}
          <Bar
            dataKey="totalDuration"
            fill="transparent"
            shape={<VehicleTripsBar />}
            barSize={30}
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Single custom tooltip positioned absolutely */}
      {hoveredTrip && (
        <div
          style={{
            position: "absolute",
            left: mousePosition.x + 10,
            top: mousePosition.y - 60,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "10px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            zIndex: 1000,
            pointerEvents: "none",
            maxWidth: "250px",
          }}
        >
          <p style={{ margin: "0 0 4px 0", fontWeight: "bold" }}>
            {hoveredTrip.vehicleName} - {hoveredTrip.tripId}
          </p>
          <p style={{ margin: "2px 0" }}>{t("Route")}: {hoveredTrip.routeName}</p>
          <p style={{ margin: "2px 0" }}>{t("Start")}: {hoveredTrip.startTime}</p>
          <p style={{ margin: "2px 0" }}>{t("End")}: {hoveredTrip.endTime}</p>
          <p style={{ margin: "2px 0" }}>{t("Status")}: {t(hoveredTrip.status)}</p>
          <p style={{ margin: "2px 0 0 0" }}>
            {t("Duration")}: {Math.round(hoveredTrip.duration)} {t("minutes")}
          </p>
        </div>
      )}

      {/* Legend */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{ width: 16, height: 16, backgroundColor: "#28a745", mr: 1 }}
          />
          <Typography variant="caption">{t("On Time")}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{ width: 16, height: 16, backgroundColor: "#ffc107", mr: 1 }}
          />
          <Typography variant="caption">{t("Delayed")}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HorizontalBarTimeline;
